import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuthStore } from '../../auth/stores/authStore';
import { getCurrentOrganisation } from '../api/organizationApi';
import { STALE_TIMES } from '../../../shared/config/cache';
import { getTokenIdentifier } from '@billda/shared-ui';

export const organizationKeys = {
  all: ['organization'] as const,
  current: (tokenIdentifier: string | null) =>
    [...organizationKeys.all, 'current', tokenIdentifier] as const,
};

/**
 * Custom error class to indicate unauthorized access.
 * Used to prevent retries and trigger logout.
 */
class UnauthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

export function useOrganization() {
  const { token, isAuthenticated, logout } = useAuthStore();
  const tokenIdentifier = token ? getTokenIdentifier(token) : null;

  const query = useQuery({
    queryKey: organizationKeys.current(tokenIdentifier),
    queryFn: async () => {
      try {
        return await getCurrentOrganisation();
      } catch (error) {
        // If unauthorized (401), trigger logout and throw special error
        if (
          axios.isAxiosError(error) &&
          (error.response?.status === 401 || error.response?.status === 403)
        ) {
          console.log(
            '🔒 Organization endpoint returned 401 - logging out user'
          );
          logout('organization_unauthorized');
          throw new UnauthorizedError('Session expired. Please log in again.');
        }
        throw error;
      }
    },
    enabled: isAuthenticated,
    staleTime: STALE_TIMES.ORGANIZATION_DATA,
    throwOnError: false, // Don't throw, let component handle
    // Don't retry on unauthorized errors - the user needs to log in again
    retry: (failureCount, error) => {
      // Never retry unauthorized errors
      if (error instanceof UnauthorizedError) {
        return false;
      }
      // Default retry behavior for other errors (max 3 retries)
      return failureCount < 3;
    },
  });

  return query;
}
